import React from 'react'
import { useIntl } from 'react-intl'
import { Tooltip, Icon } from '@mui/material'
import { withStyles } from '@mui/styles'

import { fileSizeTransform } from '../logics/file-size-transform.logics'
import { uploadTooltipStyles } from './upload-tooltip.styles'

/**
 * @typedef {object} FileConfig
 * @property {string[]} [accept] - Array de tipos MIME aceptados (ej. ['image/png', 'application/pdf']).
 * @property {number} [maxSize] - Tamaño máximo del archivo en bytes.
 */

/**
 * @typedef {object} UploadTooltipProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {FileConfig} file - Configuración del archivo con restricciones de tipo y tamaño.
 */

/**
 * Componente de tooltip informativo para restricciones de subida de archivos.
 * 
 * Muestra un icono de ayuda con un tooltip que informa al usuario sobre:
 * - **Tamaño máximo**: Tamaño máximo permitido del archivo (formateado en KB/MB/GB)
 * - **Tipos aceptados**: Extensiones de archivo permitidas
 * 
 * El componente no se renderiza si no hay configuración de archivo o si no hay
 * restricciones de tipo o tamaño definidas.
 * 
 * @component
 * @example
 * const MyUploadField = () => {
 *   const fileConfig = {
 *     accept: ['image/png', 'image/jpeg', 'application/pdf'],
 *     maxSize: 5 * 1024 * 1024, // 5MB
 *   };
 * 
 *   return (
 *     <div>
 *       <label>Subir archivo</label>
 *       <UploadTooltip file={fileConfig} />
 *       <input type="file" />
 *     </div>
 *   );
 * };
 * 
 * @param {UploadTooltipProps} props - Las props del componente UploadTooltip.
 */
export const UploadTooltip = withStyles(uploadTooltipStyles)(({ classes, file, ...props }) => {
  const { formatMessage } = useIntl()

  if (!file) return null
  if (!file.accept && !file.maxSize) return null

  return (
    <Tooltip
      data-role="upload tooltip"
      title={
        <div data-role="upload info">
          <div className={classes.container}>
            <span className={classes.title}>
              {formatMessage({ id: 'documents.tooltip.maximumSize' })}
            </span>
            <span className={classes.description} data-role="size info">
              {fileSizeTransform(file.maxSize)}
            </span>
          </div>
          <div className={classes.container}>
            <span className={classes.title}>
              {formatMessage({ id: 'documents.tooltip.acceptedFiles' })}
            </span>
            {file.accept ? (
              <span className={classes.description} data-role="type info">
                {file.accept.map((type) => type.split('/').pop()).join(', ')}
              </span>
            ) : null}
          </div>
        </div>
      }
    >
      <div data-role="upload help label">
        <Icon fontSize="inherit">help_outline</Icon>
      </div>
    </Tooltip>
  )
})
